import type {
  CollectedLeadFormFields,
  LeadFormCollectedInput,
  LeadFormCollectedStep,
} from "./types";

import { POSTAL_CODE_FIELD_NAME } from "./lead-form-field-names";

const STANDARD_INPUT_OUTPUT_FIELD_NAMES: Record<string, string> = {
  smsOptIn: "consentTerms",
};

function isStandardInput(
  input: LeadFormCollectedInput,
  inputType: string,
): boolean {
  return input._type === "standardInput" && input.inputType === inputType;
}

function isVisiblePostalCodeInput(input: LeadFormCollectedInput): boolean {
  return isStandardInput(input, POSTAL_CODE_FIELD_NAME);
}

function isChoiceInput(input: LeadFormCollectedInput): boolean {
  return input._type === "radioInput" || input._type === "multiSelectInput";
}

function getStandardInputFieldName(
  input: LeadFormCollectedInput,
): string | null {
  if (!input.inputType) {
    return null;
  }

  return STANDARD_INPUT_OUTPUT_FIELD_NAMES[input.inputType] ?? input.inputType;
}

function getStepInputFieldName(input: LeadFormCollectedInput): string | null {
  if (input._type === "standardInput") {
    return getStandardInputFieldName(input);
  }

  if (isChoiceInput(input) && input.fieldName) {
    return input.fieldName;
  }

  return null;
}

function collectOptionValues(input: LeadFormCollectedInput): string[] {
  if (!input.options) {
    return [];
  }

  return input.options.flatMap((option) => {
    if (!option?.value) {
      return [];
    }

    return [option.value];
  });
}

function collectLeadFormFields(
  steps: LeadFormCollectedStep[] | null | undefined,
): CollectedLeadFormFields {
  const choiceFieldNames = new Set<string>();
  const fieldNames = new Set<string>();
  const optionValuesByFieldName: Record<string, string[]> = {};
  let hasVisiblePostalCode = false;

  for (const step of steps ?? []) {
    for (const input of step.inputs ?? []) {
      if (!input) {
        continue;
      }

      if (isVisiblePostalCodeInput(input)) {
        hasVisiblePostalCode = true;
      }

      const fieldName = getStepInputFieldName(input);
      if (fieldName) {
        fieldNames.add(fieldName);
      }

      if (isChoiceInput(input) && input.fieldName) {
        choiceFieldNames.add(input.fieldName);
        const values = collectOptionValues(input);
        const existing = optionValuesByFieldName[input.fieldName] ?? [];
        optionValuesByFieldName[input.fieldName] = [...existing, ...values];
      }
    }
  }

  return {
    choiceFieldNames: [...choiceFieldNames],
    fieldNames: [...fieldNames],
    hasVisiblePostalCode,
    optionValuesByFieldName,
  };
}

export {
  collectLeadFormFields,
  getStepInputFieldName,
  isVisiblePostalCodeInput,
};
